import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { FixedSizeList as List, ListChildComponentProps, ListOnScrollProps } from 'react-window'
import styled from '@emotion/styled'
import { Grid, GridProps, Typography } from '@mui/material'
import { Colors, Shapes, printStyle } from 'src/components/theme'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { rgbaStrOpacity } from 'src/utils/colors'
import { GRID_TABLE_DIV_ID, GRID_TABLE_ROWS_DIV_ID, VIRTUALIZED_TABLE_CLASSNAME } from 'src/utils/html-to-image'
import { ArrowSortUp } from '@emotion-icons/fluentui-system-filled/ArrowSortUp'
import { ArrowSortDown } from '@emotion-icons/fluentui-system-filled/ArrowSortDown'

type OnScrollNormal = (event: React.UIEvent<HTMLDivElement, UIEvent>) => void
type OnScrollVirtualized = (props: ListOnScrollProps) => void
type UseScrollValue = {
  scrollTop: number
  onScroll: OnScrollVirtualized | OnScrollNormal
}
export function useScrollTop(virtualized: boolean): UseScrollValue {
  const [scrollTop, setScrollTop] = useState(0)

  const onScrollVirtualized = (props: ListOnScrollProps) => {
    setScrollTop(props.scrollOffset)
  }

  const onScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    setScrollTop(event.currentTarget.scrollTop)
  }

  return { scrollTop, onScroll: virtualized ? onScrollVirtualized : onScroll }
}

const tablePrintStyle = printStyle`
  height: auto;
  max-height: 100%;
`
const Table = styled.div<{ maxHeight?: number; virtualized: boolean }>`
  position: relative;
  background-color: ${Colors.bgWhite};
  min-height: 200px;
  max-height: ${(props) => props.maxHeight}px;
  border: 1px solid ${Colors.border};
  border-radius: ${Shapes.borderRadius}px;
  overflow-x: hidden;
  width: 100%;
  ${(props) => (props.virtualized ? 'overflow-y: hidden;' : `overflow-y: auto; ${FANCY_SCROLLBAR}`)}
  ${(props) =>
    props.virtualized &&
    `
    & > div {
      ${FANCY_SCROLLBAR}
    }
  `}
  ${tablePrintStyle}
`
const TableHeader = styled(Grid)`
  background-color: ${Colors.bgWhite};
  border: 0;
  position: sticky;
  top: 0;
  width: 100%;
  border-top-right-radius: ${Shapes.borderRadius};
  border-top-left-radius: ${Shapes.borderRadius};
  border-radius: 0;
  max-height: 64px;
  overflow: hidden;
  transition: box-shadow 0.1s ease-in-out;
  min-height: 48px;
  z-index: 2 !important;
`

const Row = styled(Grid)<{ striped?: 1 | 0; active?: 1 | 0; odd?: 1 | 0; activecolor: string }>`
  position: relative;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
  width: 100%;
  background-color: ${(props) => (props.active ? rgbaStrOpacity(props.activecolor, 0.15) : Colors.bgWhite)};
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
  border-color: ${(props) => (props.active ? props.activecolor : 'transparent')};
  box-shadow: ${(props) => (props.active ? Colors.BOX_SHADOW : 'none')};
  z-index: ${(props) => (props.active ? 1 : 0)};

  .MuiGrid-item *:not(path) {
    ${(props) => (props.active ? `color: ${Colors.textBlack};` : '')}
  }

  ${(props) =>
    props.odd &&
    `
    background-color: ${
      props.active ? rgbaStrOpacity(props.activecolor, 0.15) : props.striped ? Colors.bgMedium : Colors.bgWhite
    };
  `}
`
export const GridTableRow = (
  props: GridProps & { striped?: boolean; active?: boolean; odd?: boolean; activecolor?: string }
) => {
  return (
    <Row
      container
      {...props}
      active={props.active ? 1 : 0}
      striped={props.striped ? 1 : 0}
      odd={props.odd ? 1 : 0}
      activecolor={props.activecolor || Colors.blueDark}
    />
  )
}

const GridItem = (props: GridProps) => {
  return <Grid item {...props} />
}

const SortIconContainer = styled.div<{ persist: boolean }>`
  cursor: pointer;
  opacity: ${({ persist }) => (persist ? 1 : 0)};
  flex-shrink: 0;
  padding: ${Shapes.spacing[2]}px;
  margin-left: ${Shapes.spacing[3]}px;
`

const HeaderItem = styled(GridItem)<GridProps>`
  display: flex;
  align-items: center;
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[4]}px;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
  p {
    display: flex;
    flex: 1;
    align-items: center;
    gap: ${Shapes.spacing[1]}px;
  }

  &:hover ${SortIconContainer} {
    opacity: 1;
  }
`

export type GridTableHeaderItemProps = GridProps & {
  sorted?: boolean
  sortDirection?: string
  onSort?: () => void
}

export const GridTableHeaderItem = ({ sorted = false, sortDirection, onSort, ...props }: GridTableHeaderItemProps) => {
  const SortIcon = sortDirection === 'asc' ? ArrowSortUp : ArrowSortDown
  return (
    <HeaderItem {...props} onClick={onSort}>
      <Typography variant="textmd" color={Colors.textDark}>
        {props.children}
      </Typography>
      {onSort && (
        <SortIconContainer persist={!!sortDirection && sorted}>
          <SortIcon size={Shapes.spacing[4]} color={sortDirection ? Colors.blueDark : Colors.textDark} />
        </SortIconContainer>
      )}
    </HeaderItem>
  )
}

const Item = styled(GridItem)<{ dashed?: 1 | 0 }>`
  display: flex;
  align-items: center;
  padding: ${Shapes.spacing[3]}px ${Shapes.spacing[4]}px;
  border-right: ${(props) => (props.dashed ? `1px dashed ${Colors.borderInput}` : 'none')};
  /* max-width: fit-content; */
  &:last-of-type {
    border-right: 0;
  }
`
const ItemText = styled(Typography)`
  display: inline-flex;
  align-items: center;
  gap: ${Shapes.spacing[2]}px;
`
export const GridTableItem = (props: GridProps & { dashed?: boolean }) => {
  return (
    <Item item {...props} dashed={props.dashed ? 1 : 0}>
      {typeof props.children === 'string' || typeof props.children === 'number' ? (
        <ItemText variant="textmdm" color={Colors.textDark}>
          {props.children}
        </ItemText>
      ) : (
        props.children
      )}
    </Item>
  )
}

const bottomScrollShadowPrintStyle = printStyle`
  display: none;
`
const BottomScrollShadow = styled.div`
  position: sticky;
  bottom: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.1), transparent);
  width: 100%;
  height: 16px;
  ${bottomScrollShadowPrintStyle}
`

type BaseGridTableProps = {
  headers?: React.ReactNode
  style?: React.CSSProperties
  activeRow?: {
    id: string
    method: 'table' | 'map'
  }
}

type NormalGridTableProps = {
  virtualized?: false
  rows: React.ReactNode
  maxHeight?: number
} & BaseGridTableProps

type VirtualizedGridTableProps = {
  virtualized: true
  renderRow: (props: ListChildComponentProps) => React.ReactNode
  height: number
  itemSize: number
  itemCount: number
} & BaseGridTableProps

type GridTableProps = NormalGridTableProps | VirtualizedGridTableProps

export const GridTable: FC<GridTableProps> = (props) => {
  const tableRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<any>(null) // this is any because some types declarations are missing from react-window
  const [{ scrollEnd, scrollHeight }, setScrollConfig] = useState<{
    scrollEnd: boolean
    scrollHeight: number
  }>({
    scrollEnd: false,
    scrollHeight: 0,
  })
  const { scrollTop, onScroll } = useScrollTop(props.virtualized || false)

  const onScrollTopChange = useCallback(() => {
    if (!tableRef.current || props.virtualized) {
      return
    }

    const top = scrollTop || tableRef.current.scrollTop
    const offsetHeight = tableRef.current.offsetHeight
    const scrollHeight = tableRef.current.scrollHeight

    const scrollEnd = top + offsetHeight >= scrollHeight

    setScrollConfig({
      scrollEnd,
      scrollHeight,
    })
  }, [scrollTop, setScrollConfig, props.virtualized])

  const onScrollTopChangeVirtualized = useCallback(() => {
    if (!listRef.current || !props.virtualized) {
      return
    }

    const top = scrollTop || listRef.current._outerRef.scrollTop
    const offsetHeight = listRef.current._outerRef.offsetHeight
    const scrollHeight = listRef.current._outerRef.scrollHeight

    const scrollEnd = top + offsetHeight >= scrollHeight

    setScrollConfig({
      scrollEnd,
      scrollHeight,
    })
  }, [scrollTop, setScrollConfig, props.virtualized])

  useEffect(() => {
    onScrollTopChange()
  }, [onScrollTopChange])

  useEffect(() => {
    onScrollTopChangeVirtualized()
  }, [onScrollTopChangeVirtualized])

  useEffect(() => {
    if (!props.activeRow) {
      return
    }
    // Don't scroll if the active row was selected by clicking on the table
    if (props.activeRow.method === 'table') {
      return
    }

    const element = document.getElementById(props.activeRow.id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: props.virtualized ? 'nearest' : 'center' })
    }
  }, [props.activeRow])

  const renderTableFooter = () => {
    const tableHeight = props.virtualized ? props.height : props.maxHeight

    return (
      <>
        {tableHeight && scrollHeight && scrollHeight > tableHeight && (
          <BottomScrollShadow
            style={{
              opacity: scrollEnd ? 0 : 1,
            }}
          />
        )}
      </>
    )
  }

  return (
    <Table
      id={GRID_TABLE_DIV_ID}
      ref={tableRef}
      style={props.style}
      maxHeight={props.virtualized ? props.height : props.maxHeight}
      onScroll={props.virtualized ? undefined : (onScroll as (event: React.UIEvent<HTMLDivElement, UIEvent>) => void)}
      virtualized={props.virtualized || false}>
      <TableHeader
        style={{
          boxShadow: scrollTop > 1 ? `0px 16px 10px rgba(0, 0, 0, 0.05)` : 'none',
          borderBottom: scrollTop > 1 ? `1px solid ${Colors.border}` : 'none',
          zIndex: scrollTop > 1 && props.virtualized ? 1 : 0,
        }}
        container>
        {props.headers}
      </TableHeader>
      {!props.virtualized && (
        <div id={GRID_TABLE_ROWS_DIV_ID}>
          {props.rows}
          {renderTableFooter()}
        </div>
      )}
      {props.virtualized && (
        <>
          <List
            className={VIRTUALIZED_TABLE_CLASSNAME}
            ref={listRef}
            height={props.height}
            itemCount={props.itemCount}
            itemSize={props.itemSize}
            width="100%"
            onScroll={onScroll as OnScrollVirtualized}>
            {(childProps) => props.renderRow(childProps) as any}
          </List>
          {renderTableFooter()}
        </>
      )}
    </Table>
  )
}
